import React, { useState, useEffect, useCallback } from 'react';
import { Alert, Badge, Card, CardBody, Col, Row, Table } from 'reactstrap';
import Switch from 'react-switch';
import { toast } from 'react-toastify';
import Heading from '../common/Heading';
import Loader from '../common/Loader';
import BZAPIService from '../../services/BZAPIService';
import { adminIsReadOnlyCheck } from '../../helpers/rbac';

const DEFAULT_DISCLAIMER_TEXT =
  'I understand that AI-generated content may not always be accurate and that I should review it before use.';

// A vendor row is editable only when the API says so. The API once dropped
// allowGlobalAiSettingsEdit and a missing flag read as editable, so msftsql,
// which is configured from its own vendor page, showed live controls here
// (apps#493, apps#520). A missing flag now fails closed and warns, so a
// regression is visible in the console instead of as an editable row.
export const isVendorEditable = vendor => vendor?.allowGlobalAiSettingsEdit === true;

export const warnOnMissingEditFlags = vendors => {
  (vendors || [])
    .filter(vendor => vendor?.allowGlobalAiSettingsEdit === undefined || vendor?.allowGlobalAiSettingsEdit === null)
    .forEach(vendor =>
      console.warn(
        `Global AI Settings: vendor ${vendor.vendorId} has no allowGlobalAiSettingsEdit flag in the API response; rendering it read-only`
      )
    );
};

const GlobalAISettings = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [isReadOnly, setIsReadOnly] = useState(true);
  const [globalAi, setGlobalAi] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [savingVendorId, setSavingVendorId] = useState(null);
  const [disclaimerModalOpen, setDisclaimerModalOpen] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState(null);

  const fetchAiSettings = useCallback(async () => {
    try {
      const response = await BZAPIService.getVendorAiSettings();
      if (response) {
        setGlobalAi(response.globalAi);
        setVendors(response.vendors || []);
        warnOnMissingEditFlags(response.vendors);
      }
    } catch (error) {
      console.error('Error fetching AI settings:', error);
      toast.error('Failed to fetch AI settings');
    }
  }, []);

  const loadData = useCallback(async () => {
    try {
      setIsLoading(true);
      const myUserPermissions = await BZAPIService.getMyUserPermissions();
      setIsReadOnly(adminIsReadOnlyCheck(myUserPermissions));
      await fetchAiSettings();
    } catch (error) {
      console.error('Error loading AI settings:', error);
      toast.error('Failed to load AI settings');
      setIsReadOnly(true);
    } finally {
      setIsLoading(false);
    }
  }, [fetchAiSettings]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleGlobalToggle = async () => {
    try {
      setSavingVendorId('globalAi');
      const newEnabled = !globalAi?.aiEnabled;
      await BZAPIService.updateVendorAiSetting('globalAi', {
        aiEnabled: newEnabled,
        disclaimerAccepted: globalAi?.disclaimerAccepted || false
      });
      toast.success(`Global AI ${newEnabled ? 'enabled' : 'disabled'} successfully`);
      await fetchAiSettings();
    } catch (error) {
      console.error('Error toggling global AI:', error);
      toast.error('Failed to update global AI settings');
    } finally {
      setSavingVendorId(null);
    }
  };

  const handleVendorToggle = async vendor => {
    const newEnabled = !vendor.aiEnabled;

    // If trying to enable and disclaimer not accepted, prompt to sign
    if (newEnabled && !vendor.disclaimerAccepted) {
      setSelectedVendor(vendor);
      setDisclaimerModalOpen(true);
      return;
    }

    // If trying to disable and aiRequired, show error
    if (!newEnabled && vendor.aiRequired && vendor.disclaimerAccepted) {
      toast.error('AI is required for this vendor and cannot be disabled');
      return;
    }

    try {
      setSavingVendorId(vendor.vendorId);
      await BZAPIService.updateVendorAiSetting(vendor.vendorId, {
        aiEnabled: newEnabled,
        disclaimerAccepted: vendor.disclaimerAccepted
      });
      toast.success(`AI ${newEnabled ? 'enabled' : 'disabled'} for ${vendor.name}`);
      await fetchAiSettings();
    } catch (error) {
      console.error('Error toggling vendor AI:', error);
      toast.error(`Failed to update AI settings for ${vendor.name}`);
    } finally {
      setSavingVendorId(null);
    }
  };

  const handleSignDisclaimer = async vendor => {
    setSelectedVendor(vendor);
    setDisclaimerModalOpen(true);
  };

  const confirmDisclaimer = async () => {
    if (!selectedVendor) return;

    try {
      setSavingVendorId(selectedVendor.vendorId);
      await BZAPIService.updateVendorAiSetting(selectedVendor.vendorId, {
        aiEnabled: true,
        disclaimerAccepted: true
      });
      toast.success(`Disclaimer accepted and AI enabled for ${selectedVendor.name}`);
      setDisclaimerModalOpen(false);
      setSelectedVendor(null);
      await fetchAiSettings();
    } catch (error) {
      console.error('Error signing disclaimer:', error);
      toast.error('Failed to sign disclaimer');
    } finally {
      setSavingVendorId(null);
    }
  };

  const formatDate = dateString => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  const isControlsDisabled = !globalAi?.aiEnabled || isReadOnly;

  if (isLoading) {
    return <Loader text="Loading AI settings..." />;
  }

  return (
    <>
      <Heading
        title="Global AI Settings"
        subtitle="Manage AI configuration across all vendors"
        className="mb-4 mt-3"
        icon="robot"
      />

      {isReadOnly && (
        <Alert color="info">Note: Your user only has read-only access. You will not be able to save any updates.</Alert>
      )}

      {/* Global AI Section */}
      <Card className="mb-4">
        <CardBody className="fs--1">
          <Row className="align-items-center">
            <Col>
              <h5 className="mb-0">Global AI</h5>
              <p className="text-muted mb-0 mt-1">
                Enable or disable AI features globally. When disabled, all vendor AI controls are locked.
              </p>
            </Col>
            <Col xs="auto" className="d-flex align-items-center">
              <Badge color={globalAi?.aiEnabled ? 'success' : 'secondary'} className="me-3" pill>
                {globalAi?.aiEnabled ? 'Enabled' : 'Disabled'}
              </Badge>
              {savingVendorId === 'globalAi' ? (
                <div className="spinner-border spinner-border-sm text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              ) : (
                <Switch
                  checked={globalAi?.aiEnabled || false}
                  onChange={handleGlobalToggle}
                  disabled={isReadOnly || savingVendorId !== null}
                  onColor="#253746"
                  uncheckedIcon={false}
                  checkedIcon={false}
                  height={20}
                  width={48}
                />
              )}
            </Col>
          </Row>
        </CardBody>
      </Card>

      {/* Vendors Section */}
      <Card>
        <CardBody className="fs--1">
          <h5 className="mb-3">AI-Supported Vendors</h5>

          {!globalAi?.aiEnabled && (
            <Alert color="warning" className="mb-3">
              Global AI is disabled. Enable Global AI above to configure vendor AI settings.
            </Alert>
          )}

          <Table responsive bordered striped>
            <thead>
              <tr>
                <th>Vendor</th>
                <th>AI Required</th>
                <th>AI Status</th>
                <th>Disclaimer</th>
                <th>AI Usage</th>
                <th className="text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              {vendors.map(vendor => {
                const vendorEditable = isVendorEditable(vendor);
                const vendorDisabled = isControlsDisabled || !vendorEditable;
                return (
                  <tr key={vendor.vendorId} className={vendorDisabled ? 'opacity-50' : ''}>
                    <td className="fw-bold">
                      {vendor.name}
                      {!vendorEditable && (
                        <div className="small text-muted fst-italic mt-1">
                          This setting must be configured from the vendor configuration page.
                        </div>
                      )}
                    </td>
                    <td>
                      <Badge color={vendor.aiRequired ? 'warning' : 'info'} pill>
                        {vendor.aiRequired ? 'Required' : 'Optional'}
                      </Badge>
                    </td>
                    <td>
                      <Badge color={vendor.aiEnabled ? 'success' : 'secondary'} pill>
                        {vendor.aiEnabled ? 'Enabled' : 'Disabled'}
                      </Badge>
                    </td>
                    <td>
                      {vendor.disclaimerAccepted ? (
                        <div>
                          <Badge color="success" pill className="mb-1">
                            Accepted
                          </Badge>
                          {vendor.disclaimerAcceptedBy && (
                            <div className="small text-muted">by {vendor.disclaimerAcceptedBy}</div>
                          )}
                          {vendor.disclaimerAcceptedAt && (
                            <div className="small text-muted">{formatDate(vendor.disclaimerAcceptedAt)}</div>
                          )}
                        </div>
                      ) : (
                        <Badge color="secondary" pill>
                          Not Accepted
                        </Badge>
                      )}
                    </td>
                    <td className="small">{vendor.aiApplicationDescription || 'N/A'}</td>
                    <td className="text-end">
                      <div className="d-flex align-items-center justify-content-end gap-2">
                        {!vendor.disclaimerAccepted && (
                          <button
                            className="btn btn-sm btn-primary"
                            onClick={() => handleSignDisclaimer(vendor)}
                            disabled={vendorDisabled || savingVendorId !== null}
                          >
                            Sign Disclaimer
                          </button>
                        )}
                        {savingVendorId === vendor.vendorId ? (
                          <div className="spinner-border spinner-border-sm text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                          </div>
                        ) : (
                          <Switch
                            checked={vendor.aiEnabled || false}
                            onChange={() => handleVendorToggle(vendor)}
                            disabled={vendorDisabled || savingVendorId !== null}
                            onColor="#617E71"
                            uncheckedIcon={false}
                            checkedIcon={false}
                            height={14}
                            width={28}
                          />
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {vendors.length === 0 && (
                <tr>
                  <td colSpan="6" className="text-center">
                    No AI-supported vendors found
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </CardBody>
      </Card>

      {/* Disclaimer Modal */}
      {disclaimerModalOpen && selectedVendor && (
        <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">AI Disclaimer Agreement - {selectedVendor.name}</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setDisclaimerModalOpen(false);
                    setSelectedVendor(null);
                  }}
                />
              </div>
              <div className="modal-body">
                <p>{selectedVendor.disclaimerText || DEFAULT_DISCLAIMER_TEXT}</p>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setDisclaimerModalOpen(false);
                    setSelectedVendor(null);
                  }}
                  disabled={savingVendorId !== null}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={confirmDisclaimer}
                  disabled={savingVendorId !== null}
                >
                  {savingVendorId ? 'Saving...' : 'I Agree'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default GlobalAISettings;
